import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SelectModule } from 'primeng/select';
import { ReportModel } from './model/report-option.model';
import { FluidModule } from 'primeng/fluid';
import { DataService } from '../services/data.service';
import { AgGridAngular } from 'ag-grid-angular';
import { GridReadyEvent, ColDef, GridApi } from 'ag-grid-enterprise';
import { UrlParameters } from '../services/model/url-parameters.model';
import { UserService } from '../services/user.service';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { CheckboxChangeEvent, CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { CacheService } from '../services/cache.service';
import { NotificationMessage } from '../services/model/notification-message.model';
import { DialogType } from '../services/model/dialog-type.enum';
import { FloatLabel } from 'primeng/floatlabel';
import { ExecutingTaskMonitorService } from '../services/executing-task-monitor.service';
import { FormsModule } from '@angular/forms';
import { GaAnalyticsService } from '../services/ga-analytics.service';

@Component({
  selector: 'app-reports',
  imports: [
    SelectModule,
    FluidModule,
    AgGridAngular,
    CardModule,
    CommonModule,
    DatePickerModule,
    CheckboxModule,
    ButtonModule,
    FloatLabel,
    FormsModule,
  ],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss',
  standalone: true,
})
export class ReportsComponent implements OnInit {
  @ViewChild('cdiReportGrid') agGrid!: AgGridAngular;

  public reportConfiguration?: ReportModel;
  public reportData: any[] = [];
  public columnDefinition: ColDef[] = [];
  public rowGroupPanelShow: 'always' | 'onlyWhenGrouping' | 'never' = 'always';
  public groupDefaultExpanded = 1;
  private _dealerAccountNumber?: number;
  public beginDate?: Date;
  public endDate?: Date;
  public isProformaCheckboxChecked?: boolean;
  private _selectedYear?: string;
  private _gridApi!: GridApi;

  constructor(
    private readonly _dataService: DataService,
    private readonly _user: UserService,
    private readonly _executingTaskSvc: ExecutingTaskMonitorService,
    public readonly cache: CacheService,
    private readonly _googleAnalyticsService: GaAnalyticsService,
  ) {}

  ngOnInit(): void {}

  public onChangeAvailableReports(reportModel: ReportModel): void {
    if (reportModel !== null && reportModel !== undefined) {
      this.columnDefinition = [];
      this.reportData = [];
      this.reportConfiguration = reportModel;
      this.setBeginAndEndDate();
      this._dealerAccountNumber = undefined;
      this.isProformaCheckboxChecked = undefined;
      this._selectedYear = undefined;
    }
  }

  private setBeginAndEndDate(): void {
    let today = new Date();
    this.beginDate = today;

    let sixMonthsFromToday = new Date();
    sixMonthsFromToday.setDate(sixMonthsFromToday.getDate() + 180);
    this.endDate = sixMonthsFromToday;
  }

  public onChangeAccountName(args: any): void {
    this._dealerAccountNumber = args.accountNumber;
  }

  public onChangeYearList(args: any): void {
    this._selectedYear = args.year;
  }

  public onChangeproductList(args: any): void {}

  public onViewReportButtonClick(): void {
    this._googleAnalyticsService.logEvent('feature_used', {
      utcDateTime: new Date(),
      userType: this._user.userProfile.accountNumber,
      Record: {
        Company: `${this._user.userProfile.companyCode}`,
        Record: { Report: `${this.reportConfiguration?.name}` },
      },
    });
    this.getDataForReport();
  }

  private getDataForReport(): void {
    this._executingTaskSvc.executingTasks = [];
    this._dataService
      .getAllWithQueryParameters<any>(
        this.reportConfiguration?.apiEndPoint!,
        this.getQueryParameter(),
        `Get data for report ${this.reportConfiguration?.name} ...`,
        false,
      )
      .subscribe({
        next: (data) => {
          if (data.length != 0) {
            this.setGridColumns();
            this.reportData = data;
          } else {
            this.cache.showDialog.next(
              new NotificationMessage(DialogType.error, 'No Data', [
                'Data not available for this report.',
              ]),
            );
          }
        },
        error: (err) => {
          throw err;
        },
      });
  }

  private setGridColumns(): void {
    this.columnDefinition = [];

    this.reportConfiguration?.columnConfigurations?.forEach((column) => {
      let filterType: string = '';
      if (column.allowFiltering) {
        if (column.dataType.toLowerCase() == 'text') {
          filterType = 'agSetColumnFilter';
        } else if (column.dataType.toLowerCase() == 'number') {
          filterType = 'agNumberColumnFilter';
        } else if (column.dataType.toLowerCase() == 'date') {
          filterType = 'agDateColumnFilter';
        } else {
          filterType = 'agSetColumnFilter';
        }
      }
      this.columnDefinition.push({
        field: column.field,
        cellDataType: column.dataType,
        headerName: column.headerText,
        resizable: true,
        filter: column.allowFiltering ? filterType : false,
        filterParams: {
          excelMode: 'windows',
          buttons: ['apply', 'clear', 'reset', 'cancel'],
        },
        enableRowGroup: column.allowGrouping,
      });
    });
  }

  private setColumnAlignment(textAlign: string): any {
    if (textAlign.toLowerCase() == 'left') {
      return 'Left';
    } else if (textAlign.toLowerCase() == 'right') {
      return 'Right';
    } else if (textAlign.toLowerCase() == 'center') {
      return 'Center';
    } else if (textAlign.toLowerCase() == 'justify') {
      return 'Justify';
    }
  }

  private getQueryParameter(): UrlParameters[] {
    let urlParameters: UrlParameters[] = [];
    if (this.reportConfiguration?.isAccountNumberRequired) {
      urlParameters.push(
        new UrlParameters('AccountNumber', this.getAccountNumber()),
      );
    }
    if (this.reportConfiguration?.isCommissionCodeRequired) {
      urlParameters.push(
        new UrlParameters(
          'CommissionCode',
          this.getCommissionCode().toString(),
        ),
      );
    }

    urlParameters.push(new UrlParameters('CompanyCode', this.getCompanyCode()));

    if (this.reportConfiguration?.branchPlantRequired) {
      urlParameters.push(
        new UrlParameters('BranchPlant', this.getBranchPlant()),
      );
    }
    if (this.reportConfiguration?.showBeginDate) {
      urlParameters.push(new UrlParameters('BeginDate', this.getBeginDate()));
    }
    if (this.reportConfiguration?.showEndDate) {
      urlParameters.push(new UrlParameters('EndDate', this.getEndDate()));
    }
    if (this.reportConfiguration?.showPerformaCheckBox) {
      urlParameters.push(
        new UrlParameters('Proforma', this.getProformaStatus()),
      );
    }
    if (this.reportConfiguration?.showProductLineDropDown) {
      urlParameters.push(
        new UrlParameters('ProductLine', this.getProductLine()),
      );
    }
    if (this.reportConfiguration?.showYearDropDown) {
      urlParameters.push(new UrlParameters('Year', this.getYear()));
    }
    return urlParameters;
  }

  private getAccountNumber(): string {
    if (
      this._dealerAccountNumber === null ||
      this._dealerAccountNumber === undefined
    ) {
      throw Error(
        'Dealer account is required; please select dealer from list and try again',
      );
    }
    return this._dealerAccountNumber.toString();
  }

  private getCommissionCode(): number {
    if (this._user.userProfile.isEmployee) {
      return 0;
    } else if (this._user.userProfile.isSalesRep) {
      return this._user.userProfile.commissionCode!;
    } else {
      return this.cache.getCommissionCodeForAccount(
        this._dealerAccountNumber as any,
      );
    }
  }

  private getCompanyCode(): string {
    if (this._user.userProfile?.companyCode == undefined) {
      throw Error(
        'Unable to query Company Code Please refresh browser and try again later',
      );
    } else {
      return this._user.userProfile.companyCode;
    }
  }

  private getBranchPlant(): string {
    if (
      this._user.userProfile.branchPlant === null ||
      this._user.userProfile.branchPlant === undefined
    ) {
      throw new Error('Branch Plant not defined.');
    } else {
      return this._user.userProfile.branchPlant;
    }
  }

  private getBeginDate(): string {
    if (this.beginDate === null || this.beginDate === undefined) {
      throw Error(
        'Begin Date is required. Please select desired begin date and try again.',
      );
    } else {
      if (this.endDate == null || this.endDate === undefined) {
        throw Error(
          'End Date is required. Please select desired begin date and try again.',
        );
      } else {
        const unixBeginDate = Math.floor(this.beginDate.getTime() / 1000);
        const unixEndDate = Math.floor(this.endDate.getTime() / 1000);

        if (unixBeginDate > unixEndDate) {
          throw Error(
            'Begin Date is greater than End Date. Please select adjust Begin Date and try again.',
          );
        } else {
          return `${this.beginDate.getFullYear()}-
          ${this.getMonthName(this.beginDate.getMonth())}-
          ${this.beginDate.getDate()}`;
        }
      }
    }
  }

  private getEndDate(): string {
    if (this.endDate === null || this.endDate === undefined) {
      throw Error(
        'End Date is required <br/> Please select end date and try again.',
      );
    }
    return `${this.endDate.getFullYear()}-
    ${this.getMonthName(this.endDate.getMonth())}-
    ${this.endDate.getDate()}`;
  }

  private getMonthName(month: number): string {
    let monthName: string = '';
    if (month === 0) {
      monthName = 'January';
    } else if (month === 1) {
      monthName = 'February';
    } else if (month === 2) {
      monthName = 'March';
    } else if (month === 3) {
      monthName = 'April';
    } else if (month === 4) {
      monthName = 'May';
    } else if (month === 5) {
      monthName = 'June';
    } else if (month === 6) {
      monthName = 'July';
    } else if (month === 7) {
      monthName = 'August';
    } else if (month === 8) {
      monthName = 'September';
    } else if (month === 9) {
      monthName = 'October';
    } else if (month === 10) {
      monthName = 'November';
    } else if (month === 11) {
      monthName = 'December';
    }
    return monthName;
  }

  private getProformaStatus(): string {
    return this.isProformaCheckboxChecked ? '1' : '0';
  }

  private getYear(): string {
    if (this._selectedYear === null || this._selectedYear === undefined) {
      throw new Error('Year is required; please select a year and try again.');
    }
    return this._selectedYear.toString();
  }

  private getProductLine(): string {
    return '';
  }

  private showWarningMessage(message: string[], title: string) {
    this.cache.showDialog.next(
      new NotificationMessage(DialogType.warning, title, message),
    );
  }

  private initializeReportGrid(): void {
    this.columnDefinition = [];
  }

  public onSelectBeginDate(args: Date) {
    this.beginDate = args;
  }

  public onSelectEndDate(args: Date) {
    this.endDate = args;
  }

  public onChangeProformaCheckbox(args: CheckboxChangeEvent) {
    this.isProformaCheckboxChecked = args.checked;
  }

  public onGridReady(params: GridReadyEvent): void {
    this._gridApi = params.api;
  }

  public onExportToExcelButtonClick(): void {
    if (this._gridApi) {
      this._gridApi.exportDataAsExcel();
    }
  }
}
